import { useEffect, useState } from 'react';

// ─────────────────────────────────────────────────────────────────────────────
// usePersistentState — useState that survives leaving the page and coming back
// (in-app navigation, a reload of the tab). Values live in sessionStorage, so
// they are per browser tab and gone when the tab closes; logout clears them.
// Storage that is full or blocked silently falls back to plain state.
//
// Object values are merged over `initial`, so a filter added later still gets
// its default when an older saved value is restored.
// ─────────────────────────────────────────────────────────────────────────────
const PREFIX = 'filters:';

const isPlainObject = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);

export const readPersisted = (key, initial) => {
  const fallback = typeof initial === 'function' ? initial() : initial;
  try {
    const raw = sessionStorage.getItem(PREFIX + key);
    if (raw == null) return fallback;
    const saved = JSON.parse(raw);
    return isPlainObject(fallback) && isPlainObject(saved) ? { ...fallback, ...saved } : saved;
  } catch {
    return fallback;
  }
};

// startWith: when given (not undefined), start from it instead of the saved
// value — e.g. a deep link that must open a specific tab. It is then saved.
export default function usePersistentState(key, initial, startWith) {
  const [value, setValue] = useState(() => (startWith !== undefined ? startWith : readPersisted(key, initial)));
  useEffect(() => {
    try { sessionStorage.setItem(PREFIX + key, JSON.stringify(value)); } catch { /* keep in memory only */ }
  }, [key, value]);
  return [value, setValue];
}

// Forget every saved filter — called on logout so the next user starts clean.
export const clearPersistedFilters = () => {
  try {
    Object.keys(sessionStorage).filter(k => k.startsWith(PREFIX)).forEach(k => sessionStorage.removeItem(k));
  } catch { /* nothing to clear */ }
};
